import { List } from 'immutable';
import moment from 'moment';
import { of } from 'rxjs';
import { complement } from 'ramda';
import { sortByActivityDate, getPastPaymentDate } from './payment-utils';
import { parseFrequency } from './standing-orders';
import { HistoryRecord, buildHistoryRecordsFromAudits } from './audit-history';
var failedStates = ['DECLINED', 'BLOCKED', 'FAILED'];
var grantedStates = ['ACCEPTED', 'AUTHORIZED', 'SUBMITTING', 'SUBMITTED', 'ACTIVE', 'SUCCEEDED', 'CONSUMED', 'CANCELLED'];
var STATUS_LABELS = {
  ACCEPTED: 'created',
  AUTHORIZED: 'confirmed',
  CONSUMED: 'confirmed',
  DECLINED: 'declined',
  SUBMITTING: 'initiated',
  SUBMITTED: 'in progress',
  FAILED: 'failed',
  SUCCEEDED: 'successful',
  BLOCKED: 'blocked',
  CANCELLED: 'cancelled'
};
var HISTORY_LABELS = {
  ACCEPTED: 'Standing order created',
  AUTHORIZED: 'Confirmed',
  CONSUMED: 'Confirmed',
  DECLINED: 'Payment declined',
  SUBMITTING: 'Payment initiated',
  SUBMITTED: 'Payment in progress',
  FAILED: 'Payment failed',
  SUCCEEDED: 'Payment successful',
  BLOCKED: 'Request blocked by Bank',
  CANCELLED: 'Standing order cancelled'
};
var isGranted = function isGranted(standingOrder) {
  return grantedStates.indexOf(standingOrder.getIn(['activity', 'status'], standingOrder.get('status', standingOrder.get('grouping')))) !== -1;
};
function getStatusName(standingOrder) {
  var status = getStatus(standingOrder);
  if (failedStates.indexOf(status) !== -1) return 'FAILED';
  if (grantedStates.indexOf(status) === -1) return 'SUCCEEDED';
}
var processStandingOrderConsents = function processStandingOrderConsents(standingOrders) {
  return standingOrders.map(function (standingOrder) {
    var status = getStatus(standingOrder);
    var firstPaymentDate = getFirstPaymentDate(standingOrder);
    var finalPaymentDate = standingOrder.getIn(['payload', 'finalPaymentDate']);
    var frequency = standingOrder.getIn(['payload', 'frequency']);
    var firstPaymentAmount = parsePaymentAmount(standingOrder, 'firstPaymentAmount');
    var finalPaymentAmount = parsePaymentAmount(standingOrder, 'finalPaymentAmount');
    var recurringPaymentAmount = parsePaymentAmount(standingOrder, 'recurringPaymentAmount');
    var now = Date.now();
    var nextPaymentAmount = moment(firstPaymentDate).isAfter(now) ? firstPaymentAmount : finalPaymentDate && moment(finalPaymentDate).isBefore(now) ? finalPaymentAmount || recurringPaymentAmount || firstPaymentAmount : recurringPaymentAmount || firstPaymentAmount;
    var stringAmounts = [{
      key: 'firstPaymentAmount',
      amount: firstPaymentAmount
    }, {
      key: 'finalPaymentAmount',
      amount: finalPaymentAmount
    }, {
      key: 'recurringPaymentAmount',
      amount: recurringPaymentAmount
    }];
    return mergeFormattedStringAmounts(stringAmounts, standingOrder.setIn(['payload', 'displayFrequency'], parseFrequency(frequency)).setIn(['payload', 'nextPaymentAmount'], nextPaymentAmount).setIn(['payload', 'paymentLabel'], status === 'ACTIVE' || status === 'ACCEPTED' ? 'granted' : toStatusLabel(status)).setIn(['payload', 'paymentDate'], getPastPaymentDate(standingOrder)));
  });
};
function mergeFormattedStringAmounts(stringAmounts, standingOrder) {
  if (stringAmounts.length === 0) return standingOrder;
  var _stringAmounts$ = stringAmounts[0],
    key = _stringAmounts$.key,
    amount = _stringAmounts$.amount;
  var newStandingOrder = amount ? standingOrder.setIn(['payload', key], amount) : standingOrder;
  return mergeFormattedStringAmounts(stringAmounts.slice(1), newStandingOrder);
}
function parsePaymentAmount(standingOrder, key) {
  var amountString = standingOrder.getIn(['payload', "".concat(key, "String")]);
  var amount = standingOrder.getIn(['payload', key]);
  return amountString || amount ? Number(amount) : undefined;
}
var getStatus = function getStatus(standingOrder) {
  return standingOrder.getIn(['activity', 'status'], standingOrder.get('status', standingOrder.get('grouping', 'ACTIVE')));
};
var getFirstPaymentDate = function getFirstPaymentDate(standingOrder) {
  return standingOrder.getIn(['payload', 'firstPaymentDate']);
};
var updateStandingOrdersGeneratedState = function updateStandingOrdersGeneratedState(state) {
  var standingOrders = List(state.getIn(['standingOrders', 'index']).values());
  var granted = standingOrders.filter(isGranted).sort(sortByActivityDate);
  var failed = standingOrders.filter(complement(isGranted)).sort(sortByActivityDate);
  return state.setIn(['standingOrders', 'granted'], granted).setIn(['standingOrders', 'failed'], failed);
};
var updateStandingOrdersGeneratedState$ = of(updateStandingOrdersGeneratedState);
var getHistory = function getHistory(standingOrder, activityAudits) {
  var standingOrderStatus = standingOrder.get('status');
  var includeAuditsInHistory = standingOrderStatus === 'AUTHORIZED' || standingOrderStatus === 'CONSUMED';
  var hasActivities = activityAudits.size > 0;
  var consentHistory = List.of(new HistoryRecord({
    date: standingOrder.get(hasActivities ? 'createdAt' : 'modifiedAt', standingOrder.get('createdAt')),
    label: toHistoryLabel(standingOrderStatus),
    status: standingOrderStatus
  }));
  return includeAuditsInHistory ? buildHistoryRecordsFromAudits(activityAudits, toHistoryLabel).sortBy(function (h) {
    return -h.get('date');
  }).concat(consentHistory) : consentHistory;
};
function toStatusLabel() {
  var status = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return STATUS_LABELS[status] || status.toLowerCase();
}
function toHistoryLabel() {
  var activityStatus = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return HISTORY_LABELS[activityStatus] || activityStatus.toLowerCase();
}
var updateStandingOrderHistoryState = function updateStandingOrderHistoryState(standingOrderId, activityAudits) {
  return function (state) {
    return state.updateIn(['standingOrders', 'index', standingOrderId], function (standingOrder) {
      return standingOrder.set('history', getHistory(standingOrder, activityAudits));
    }).update(updateStandingOrdersGeneratedState);
  };
};
var updateStandingOrderActivityState = function updateStandingOrderActivityState(standingOrderId, activityAudits) {
  return function (state) {
    return state.updateIn(['standingOrders', 'index', standingOrderId], function (standingOrder) {
      if (activityAudits.size > 0 && standingOrder.has('activity')) {
        var changeAt = activityAudits.reverse().getIn([0, 'changeAt']);
        return standingOrder.setIn(['activity', 'changeAt'], changeAt);
      }
      return standingOrder;
    }).update(updateStandingOrdersGeneratedState);
  };
};
export { processStandingOrderConsents, updateStandingOrdersGeneratedState$, updateStandingOrderHistoryState, updateStandingOrderActivityState, getStatusName };